import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { subscriptionAPI, authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { trackPurchase, sendServerEvent } from '../lib/metaPixel';

const SubscriptionSuccessPage = () => {
  const { t } = useTranslation();
  const { updateUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('failed');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    console.log('[SUCCESS PAGE] pollPaymentStatus called, attempt:', attempts);

    if (attempts >= maxAttempts) {
      console.log('[SUCCESS PAGE] ❌ Max attempts reached, setting status to failed');
      setStatus('failed');
      return;
    }

    try {
      console.log('[SUCCESS PAGE] Calling subscriptionAPI.getStatus with sessionId:', sessionId);
      const response = await subscriptionAPI.getStatus(sessionId);
      console.log('[SUCCESS PAGE] getStatus response:', response.data);
      
      if (response.data.payment_status === 'paid') {
        console.log('[SUCCESS PAGE] ✅ Payment confirmed as PAID');
        setStatus('success');
        // Refresh user data
        const userResponse = await authAPI.getMe();
        console.log('[SUCCESS PAGE] User data refreshed:', userResponse.data);
        updateUser(userResponse.data);
        toast.success('Subscription activated!');
        
        // Track Purchase event (Meta Pixel + Conversion API)
        console.log('[SUCCESS PAGE] Preparing Purchase event...');
        const purchaseData = {
          value: response.data.amount_total ? response.data.amount_total / 100 : 0,
          currency: response.data.currency?.toUpperCase() || 'EUR',
          plan: userResponse.data.plan || 'SmartBalloon Subscription',
          planId: response.data.plan || userResponse.data.plan || 'smartballoon_subscription',
          content_type: 'subscription',
        };
        console.log('[SUCCESS PAGE] Purchase data:', purchaseData);
        
        const eventId = trackPurchase(purchaseData);
        console.log('[SUCCESS PAGE] trackPurchase returned eventId:', eventId);
        
        if (eventId) {
          console.log('[SUCCESS PAGE] Calling sendServerEvent for CAPI...');
          sendServerEvent('Purchase', purchaseData, eventId, userResponse.data.email);
          console.log('[SUCCESS PAGE] ✅ sendServerEvent called for Purchase');
        } else {
          console.log('[SUCCESS PAGE] ❌ eventId is null/undefined, CAPI NOT sent');
        }
        
        return;
      } else if (response.data.status === 'expired') {
        console.log('[SUCCESS PAGE] ❌ Session expired');
        setStatus('failed');
        return;
      }

      // Continue polling
      console.log('[SUCCESS PAGE] Payment not yet confirmed, continuing polling...');
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error('[SUCCESS PAGE] ❌ Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 text-primary animate-spin mx-auto mb-4" />
              <CardTitle>Processing Payment...</CardTitle>
            </>
          )}
          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <CardTitle className="text-green-700">Payment Successful!</CardTitle>
            </>
          )}
          {status === 'failed' && (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <CardTitle className="text-red-700">Payment Failed</CardTitle>
            </>
          )}
        </CardHeader>
        <CardContent className="text-center space-y-4">
          {status === 'checking' && (
            <p className="text-slate-500">
              Please wait while we confirm your payment...
            </p>
          )}
          {status === 'success' && (
            <>
              <p className="text-slate-500">
                Your subscription has been activated. You now have access to all premium features.
              </p>
              <Button onClick={() => navigate('/dashboard')} data-testid="go-to-dashboard">
                Go to Dashboard
              </Button>
            </>
          )}
          {status === 'failed' && (
            <>
              <p className="text-slate-500">
                There was a problem processing your payment. Please try again.
              </p>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" onClick={() => navigate('/subscription')}>
                  Try Again
                </Button>
                <Button onClick={() => navigate('/dashboard')}>
                  Go to Dashboard
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionSuccessPage;
